import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/hooks/use-toast";
import { api } from "@/api";
import { formatDateEU, formatError } from "@/components/shared/helpers";
import { StatCard } from "@/components/shared/StatCard";

export default function OwnerStatistics({ properties }) {
  const [fromDate, setFromDate] = useState(() => `${new Date().getFullYear()}-01-01`);
  const [toDate, setToDate] = useState(() => `${new Date().getFullYear()}-12-31`);
  const [propertyId, setPropertyId] = useState("");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadStats = async () => {
    setLoading(true);
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (propertyId) params.property_id = propertyId;
      
      const res = await api.get("/owner/statistics", { params });
      setStats(res.data);
    } catch (e) {
      toast({ title: "Erreur", description: formatError(e), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="owner-statistics" className="space-y-4">
      {/* Filters */}
      <Card data-testid="owner-stats-filters-card" className="border-white/10 bg-white/[0.08]">
        <CardHeader>
          <CardTitle data-testid="owner-stats-title" className="text-slate-100">Mes statistiques</CardTitle>
          <div className="text-sm text-slate-300">Vue d&apos;ensemble de vos réservations sur la période sélectionnée.</div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
            <div className="space-y-1">
              <Label>Date début</Label>
              <Input
                data-testid="owner-stats-from-date"
                type="date"
                className="border-white/10 bg-white/[0.08]"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>Date fin</Label>
              <Input
                data-testid="owner-stats-to-date"
                type="date"
                className="border-white/10 bg-white/[0.08]"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>Logement</Label>
              <select
                data-testid="owner-stats-property"
                className="flex h-9 w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-slate-50"
                value={propertyId}
                onChange={(e) => setPropertyId(e.target.value)}
              >
                <option value="">Tous mes logements</option>
                {properties.filter((p) => !p.archived).map((p) => (
                  <option key={p.id} value={p.id}>{p.short_name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label>&nbsp;</Label>
              <Button
                data-testid="owner-stats-load-button"
                className="w-full rounded-full bg-violet-500 text-white hover:bg-violet-400"
                onClick={loadStats}
                disabled={loading}
              >
                {loading ? "Chargement..." : "Actualiser"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {stats && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
            <StatCard title="Séjours" value={stats.summary.total_stays} subtitle={`${stats.summary.tenant_stays} loc. / ${stats.summary.owner_stays} proprio`} />
            <StatCard title="Nuitées" value={stats.summary.total_nights} />
            <StatCard title="Ménages" value={stats.summary.total_cleanings} />
            <StatCard title="Kits linge" value={stats.summary.total_linen_kits} />
            <StatCard title="Lits faits" value={stats.summary.total_beds_made || 0} />
          </div>

          {/* Cleaning Progress Bar - Objective: 5 interventions per property */}
          <Card data-testid="owner-stats-cleaning-progress-card" className="border-white/10 bg-white/[0.08]">
            <CardHeader>
              <CardTitle className="text-slate-100">Progression ménages</CardTitle>
              <div className="text-sm text-slate-300">Objectif minimum : 5 interventions ménage par logement</div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {stats.by_property && stats.by_property.length > 0 ? (
                  stats.by_property.map((prop) => {
                    const objective = 5;
                    const progress = Math.min((prop.cleanings || 0) / objective * 100, 100);
                    const isComplete = (prop.cleanings || 0) >= objective;
                    return (
                      <div key={prop.property_id} className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-200">{prop.property_name}</span>
                          <span className={isComplete ? "text-emerald-400" : "text-slate-400"}>
                            {prop.cleanings || 0} / {objective} ménages
                          </span>
                        </div>
                        <div className="h-3 rounded-full bg-white/10 overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all duration-500 ${isComplete ? "bg-emerald-500" : "bg-violet-500"}`}
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center text-slate-400 py-4">Aucun logement trouvé</div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Invoices Summary */}
          <Card data-testid="owner-stats-invoices-card" className="border-white/10 bg-white/[0.08]">
            <CardHeader>
              <CardTitle className="text-slate-100">Facturation</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <StatCard title="Total facturé" value={`${stats.invoices.total_amount} €`} tone="violet" />
                <StatCard title="Payé" value={`${stats.invoices.paid_amount} €`} tone="emerald" />
                <StatCard title="En attente" value={`${stats.invoices.unpaid_amount} €`} tone="rose" />
              </div>
            </CardContent>
          </Card>

          {/* By Month */}
          {stats.by_month.length > 0 && (
            <Card data-testid="owner-stats-by-month-card" className="border-white/10 bg-white/[0.08]">
              <CardHeader>
                <CardTitle className="text-slate-100">Par mois</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="rounded-xl border border-white/10 bg-white/[0.08]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Mois</TableHead>
                        <TableHead className="text-center">Séjours</TableHead>
                        <TableHead className="text-center">Nuitées</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {stats.by_month.map((m) => (
                        <TableRow key={m.month}>
                          <TableCell className="font-medium">{m.month}</TableCell>
                          <TableCell className="text-center">{m.stays}</TableCell>
                          <TableCell className="text-center">{m.nights}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
